/**
 * Lobby screen - displays players and join info on Central Display.
 *
 * Flow:
 * 1. On mount: read credentials from playerStore
 * 2. Fetch session info (including game_config) and player list via REST
 * 3. Connect WebSocket with auth
 * 4. Subscribe to player events via usePlayerEvents
 * 5. Display player grid and join overlay
 * 6. Host sees start button (enabled when min_players reached)
 */

import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlayerGrid } from '@/components/high/lobby/PlayerGrid'
import { JoinOverlay } from '@/components/high/lobby/JoinOverlay'
import { StartButton } from '@/components/medium/StartButton'
import { PhoneLobby } from '@/components/high/lobby/PhoneLobby'
import { ConnectionOverlay } from '@/components/low/ConnectionOverlay'
import { TvCanvas } from '@/components/low/TvCanvas'
import { BrandLogo } from '@/components/low/BrandLogo'
import { SelectedModeCard } from '@/components/high/lobby/SelectedModeCard'
import { GameModeCarousel } from '@/components/high/lobby/GameModeCarousel'
import { Card } from '@/components/ui/card'
import { useSocket } from '@/hooks/useSocket'
import { usePlayerEvents } from '@/hooks/usePlayerEvents'
import { useGameLifecycle } from '@/hooks/useGameLifecycle'
import { useSessionGuard } from '@/hooks/useSessionGuard'
import { usePlayerStore, Player } from '@/stores/playerStore'
import {
  getSession,
  startGame,
  getSessionPlayers,
  getGameModeConfig,
  getGameModes,
  changeGameMode,
  GameConfig,
  GameModeListItem,
  ApiError,
  InsufficientCoinsDetail,
} from '@/services/api'
import { PaywallModal } from '@/components/high/billing/PaywallModal'
import type { Locale } from '@/types/auth'
import { useGameStore } from '@/stores/gameStore'
import { useUIStore } from '@/stores/uiStore'
import { useWalletStore } from '@/stores/walletStore'

export function LobbyScreen() {
  const [searchParams] = useSearchParams()
  const isTVMode = searchParams.get('mode') === 'tv'

  if (!isTVMode) {
    return <PhoneLobby />
  }

  return <TvLobbyScreen />
}

function resolveStatus(value: unknown): Player['connection_status'] {
  if (value === 'connecting' || value === 'connected' || value === 'disconnected' || value === 'away') {
    return value
  }
  return 'connected'
}

function TvLobbyScreen() {
  const { t } = useTranslation()
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()

  useSessionGuard()

  const playerId = usePlayerStore((s) => s.playerId)
  const sessionCode = usePlayerStore((s) => s.sessionCode)
  const sessionUuid = usePlayerStore((s) => s.sessionUuid)
  const storedGameMode = usePlayerStore((s) => s.gameMode)

  const sessionId = sessionUuid || sessionCode

  const [players, setPlayers] = useState<Player[]>([])
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null)
  const [gameMode, setGameMode] = useState<string>('bingo')
  const [gameModes, setGameModes] = useState<GameModeListItem[]>([])
  const [sessionLocale, setSessionLocale] = useState<Locale | null>(null)
  const [sessionIdFromApi, setSessionIdFromApi] = useState<string | null>(null)
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true)
  const [pingingPlayerIds, setPingingPlayerIds] = useState<string[]>([])
  const [paywallDetail, setPaywallDetail] = useState<InsufficientCoinsDetail | null>(null)
  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [isChangingMode, setIsChangingMode] = useState(false)
  const isChangingModeRef = useRef(false)
  // Portal target for the carousel: must live inside TvCanvas to share its scaling.
  const [canvasRoot, setCanvasRoot] = useState<HTMLDivElement | null>(null)

  const { isConnecting, on, reconnectState } = useSocket()

  const setPhase = useGameStore((s) => s.applyDelta)
  const addToast = useUIStore((s) => s.addToast)

  useGameLifecycle()

  // Handle player:ping event
  useEffect(() => {
    const timeouts = new Map<string, NodeJS.Timeout>()

    const unsubscribe = on<{ playerId: string }>('player:ping', (data) => {
      // Clear existing timeout for this player (reset animation)
      const existingTimeout = timeouts.get(data.playerId)
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }

      // Add/refresh player in pinging list
      setPingingPlayerIds((prev) => {
        if (prev.includes(data.playerId)) {
          return prev // Already pinging, just reset timer
        }
        return [...prev, data.playerId]
      })

      // Set new timeout to remove ping after 1s
      const timeout = setTimeout(() => {
        setPingingPlayerIds((prev) => prev.filter((id) => id !== data.playerId))
        timeouts.delete(data.playerId)
      }, 1000)

      timeouts.set(data.playerId, timeout)
    })

    return () => {
      unsubscribe()
      // Clear all timeouts on unmount
      timeouts.forEach((timeout) => clearTimeout(timeout))
    }
  }, [on])

  useEffect(() => {
    const unsubscribe = on<{ player_id: string; card: number[][] }>(
      'game:card_generated',
      (data) => {
        if (data.player_id === playerId) {
          useGameStore.getState().setBingoCard(data.card)
        }
      }
    )
    return unsubscribe
  }, [on, playerId])

  const isSessionUuid = (value: string | null | undefined): boolean => {
    if (!value) return false
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  }

  useEffect(() => {
    const unsubscribe = on<{ game_mode: string; calling_speed_seconds: number }>(
      'game:started',
      (_data) => {
        setPhase({ phase: 'playing' })
        void (async () => {
          let targetSessionId = sessionIdFromApi ?? (isSessionUuid(sessionId) ? sessionId : null)
          if (!targetSessionId && code) {
            try {
              const sessionData = await getSession(code)
              targetSessionId = sessionData.id
            } catch (error) {
              console.error('[Lobby] Failed to resolve session ID after game start:', error)
            }
          }
          if (!targetSessionId) return
          navigate(`/game/${targetSessionId}/tv`, { replace: true })
        })()
      }
    )
    return unsubscribe
  }, [on, navigate, setPhase, sessionId, sessionIdFromApi, code])

  // WS listener: game mode changed
  useEffect(() => {
    const unsubscribe = on<{ game_mode: string; min_players: number; max_players: number }>(
      'session:game_mode_changed',
      (data) => {
        setGameMode(data.game_mode)
        setGameConfig({ min_players: data.min_players, max_players: data.max_players })
      }
    )
    return unsubscribe
  }, [on])

  useEffect(() => {
    const unsubscribe = on<{
      players: Array<{
        id: string
        name: string
        is_host: boolean
        is_ready: boolean
        role: 'phone' | 'tv'
        connection_status: string
      }>
    }>('session:player_list', (data) => {
      setPlayers(
        data.players.map((p) => ({
          id: p.id,
          name: p.name,
          is_host: p.is_host,
          is_ready: p.is_ready,
          role: p.role || 'phone',
          connection_status: resolveStatus(p.connection_status),
        }))
      )
    })
    return unsubscribe
  }, [on])

  usePlayerEvents({
    onPlayerJoined: (player) => {
      setPlayers((prev) => {
        if (prev.some((p) => p.id === player.id)) return prev
        return [...prev, player]
      })
    },
    onPlayerLeft: (playerId) => {
      setPlayers((prev) => prev.filter((p) => p.id !== playerId))
    },
    onPlayerDisconnected: (playerId) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'disconnected' as const } : p
        )
      )
    },
    onPlayerReconnected: (playerId) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'connected' as const } : p
        )
      )
    },
    onPlayerAway: (playerId) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'away' as const } : p
        )
      )
    },
    onPlayerReturned: (playerId) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, connection_status: 'connected' as const } : p
        )
      )
    },
    onPlayerReady: (playerId, isReady) => {
      setPlayers((prev) =>
        prev.map((p) =>
          p.id === playerId ? { ...p, is_ready: isReady } : p
        )
      )
    },
  })

  useEffect(() => {
    // Wait for session locale before fetching modes — see AC 11: TV lobby must
    // render mode descriptions in the session's locale from the first successful
    // fetch, not the browser's Accept-Language fallback.
    if (sessionLocale === null) return
    const resolvedSessionLocale: Locale = sessionLocale

    async function fetchGameModesData() {
      try {
        const data = await getGameModes(resolvedSessionLocale)
        setGameModes(data.modes)
      } catch (error) {
        console.error('[Lobby] Failed to fetch game modes:', error)
      }
    }
    fetchGameModesData()
  }, [sessionLocale])

  useEffect(() => {
    async function fetchInitialData() {
      if (!code) return
      try {
        const sessionData = await getSession(code)
        setSessionIdFromApi(sessionData.id)
        setSessionLocale(sessionData.locale)
        setGameMode(sessionData.game_mode ?? storedGameMode ?? 'bingo')
        if (sessionData.status === 'playing') {
          navigate(`/game/${sessionData.id}/tv`, { replace: true })
          return
        }
        if (sessionData.game_mode) {
          if (sessionData.game_config) setGameConfig(sessionData.game_config)
        } else if (storedGameMode) {
          try {
            const config = await getGameModeConfig(storedGameMode)
            setGameConfig(config)
          } catch {
            if (sessionData.game_config) setGameConfig(sessionData.game_config)
          }
        } else if (sessionData.game_config) {
          setGameConfig(sessionData.game_config)
        }
        const data = await getSessionPlayers(code)
        setPlayers(data.players.map((p: Record<string, unknown>) => ({
          id: String(p.id),
          name: String(p.name),
          is_host: Boolean(p.is_host),
          is_ready: Boolean(p.is_ready),
          role: (p.role as 'phone' | 'tv') || 'phone',
          connection_status: resolveStatus(p.connection_status),
        })))
      } catch (error) {
        console.error('[Lobby] Failed to fetch initial data:', error)
      } finally {
        setIsLoadingPlayers(false)
      }
    }
    fetchInitialData()
  }, [code, navigate, storedGameMode])

  const totalCount = players.filter((p) => p.role === 'phone').length
  const minPlayers = gameConfig?.min_players ?? 2
  const maxPlayers = gameConfig?.max_players ?? 15

  const selectedMode = gameModes.find((m) => m.id === gameMode)

  const handleGameModeChange = async (newMode: string) => {
    if (!code || newMode === gameMode) return
    // A ref, not state: two clicks before the next render would both read stale state.
    if (isChangingModeRef.current) return
    isChangingModeRef.current = true
    setIsChangingMode(true)
    // Capture previous state for rollback
    const previousMode = gameMode
    const previousConfig = gameConfig
    // Optimistic update
    setGameMode(newMode)
    const modeItem = gameModes.find((m) => m.id === newMode)
    if (modeItem) {
      setGameConfig({ min_players: modeItem.min_players, max_players: modeItem.max_players })
    }
    try {
      await changeGameMode(code, newMode)
    } catch (error) {
      const apiError = error as ApiError
      addToast({
        type: 'error',
        message: apiError.message ?? t('lobby.changeModeError'),
        duration: 5000,
      })
      // Revert optimistic update on failure
      setGameMode(previousMode)
      setGameConfig(previousConfig)
    } finally {
      isChangingModeRef.current = false
      setIsChangingMode(false)
    }
  }

  const handleStartGame = async () => {
    if (!code) return
    try {
      const result = await startGame(code, gameMode)
      void useWalletStore.getState().refresh()
      setPhase({ phase: result.status as 'lobby' | 'playing' | 'game_over' })
      const targetSessionId = result.id ?? sessionId ?? sessionIdFromApi
      if (!targetSessionId) return
      navigate(`/game/${targetSessionId}/tv`, { replace: true })
    } catch (error) {
      const apiError = error as ApiError
      if (apiError.insufficientCoinsDetail) {
        setPaywallDetail(apiError.insufficientCoinsDetail)
        return
      }
      addToast({
        type: 'error',
        message: apiError.message ?? t('lobby.startGameError'),
        duration: 5000,
      })
    }
  }

  if (isLoadingPlayers || isConnecting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-16 w-16 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  /** Full-width call to action sitting directly under the main column. */
  const startAction = (
    <div className="flex-shrink-0">
      <div className={totalCount >= minPlayers ? 'animate-in fade-in slide-in-from-bottom-4 duration-700' : ''}>
        <StartButton
          disabled={totalCount < minPlayers}
          totalCount={totalCount}
          minPlayers={minPlayers}
          onClick={handleStartGame}
          compact
        />
      </div>
    </div>
  )

  return (
    <>
      {/* Rendered outside the canvas: a scaled ancestor would become the containing
          block for its fixed positioning and shrink it with the canvas. */}
      {reconnectState !== "connected" && <ConnectionOverlay state={reconnectState} />}
      <TvCanvas>
    <div ref={setCanvasRoot} className="h-full w-full bg-background text-foreground flex flex-col p-12 overflow-hidden relative font-sans">
      {/* Header */}
      <header className="flex justify-between items-start mb-16 relative z-10">
        <div className="space-y-2">
          <h1>
            <BrandLogo className="h-16" />
          </h1>
          <div className="flex items-center gap-4 text-primary font-bold uppercase tracking-[0.3em] text-sm">
            <div className="w-2 h-2 bg-primary" />
            <span>{t('lobby.active')}</span>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">{t('lobby.waitingForUnits')}</span>
          </div>
        </div>

        <Card className="bg-card/60 backdrop-blur-md border-border px-8 py-4 rounded-2xl flex items-center gap-8">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground font-black mb-1">{t('lobby.connectedUnits')}</p>
            <p className="text-3xl font-black tracking-widest text-foreground" data-testid="lobby-player-count">
              {totalCount}<span className="text-muted-foreground mx-2">/</span>
              {gameConfig === null
                ? <span className="inline-block bg-muted animate-pulse rounded w-8 h-8 align-middle" />
                : maxPlayers}
            </p>
          </div>
          <div className="h-12 w-[1px] bg-border" />
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground font-black mb-1">{t('lobby.minRequired')}</p>
            {gameConfig === null
              ? <div className="bg-muted animate-pulse rounded w-10 h-8 ml-auto mt-1" />
              : <p className={`text-3xl font-black tracking-widest ${totalCount >= minPlayers ? 'text-primary' : 'text-muted-foreground'}`}>
                  {minPlayers}
                </p>}
          </div>
        </Card>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow min-h-0 flex gap-12 relative z-10">
        {/* Player Grid + start button (2/3) */}
        <div className="flex-[2] min-w-0 min-h-0 flex flex-col gap-6">
          <div className="flex-1 min-h-0">
            <PlayerGrid
              players={players}
              currentPlayerId={playerId || undefined}
              pingingPlayerIds={pingingPlayerIds}
              layout="tv"
            />
          </div>
          {startAction}
        </div>

        {/* Join info + selected game mode (1/3) */}
        <div className="flex-[1] min-w-0 min-h-0 flex flex-col gap-6">
          <div className="flex-1 min-h-0">
            <JoinOverlay code={code || ''} />
          </div>
          {gameModes.length > 0 && (
            <SelectedModeCard
              mode={selectedMode}
              isChanging={isChangingMode}
              onChangeClick={() => setIsCarouselOpen(true)}
            />
          )}
        </div>
      </main>

      {isCarouselOpen && canvasRoot !== null && gameModes.length > 0 && (
        <GameModeCarousel
          open
          modes={gameModes}
          selectedModeId={gameMode}
          playerCount={totalCount}
          container={canvasRoot}
          onSelect={(modeId) => {
            setIsCarouselOpen(false)
            void handleGameModeChange(modeId)
          }}
          onClose={() => setIsCarouselOpen(false)}
        />
      )}

      {paywallDetail && (
        <PaywallModal
          open
          onClose={() => setPaywallDetail(null)}
          trigger="insufficient_coins"
          plans={paywallDetail.upgrade_options.map((option) => ({
            id: option.code,
            name: option.name,
            price_pln: option.price_pln,
            coins: option.coins,
            days: option.days,
          }))}
          message={paywallDetail.message}
        />
      )}
    </div>
      </TvCanvas>
    </>
  )
}
